import { startTransition, useEffect, useMemo, useState } from "react";
import { useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import type { DepartmentRecord, ProfessorIndexRecord } from "../../shared/types";
import { StaticDataState } from "../components/StaticDataState";
import { prepareProfessorSearchEntries, searchProfessorEntries } from "../lib/search";
import { fetchDepartments, fetchProfessorIndex } from "../lib/static-data";
import { useDebouncedValue } from "../lib/use-debounced-value";
import { useInfiniteResults } from "../lib/use-infinite-results";

const DEFAULT_VISIBLE_PROFESSORS = 30;
const SEARCH_BATCH_SIZE = 48;

export function ProfessorsPage() {
  const [params, setParams] = useSearchParams();
  const [professors, setProfessors] = useState<ProfessorIndexRecord[]>([]);
  const [departments, setDepartments] = useState<DepartmentRecord[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [query, setQuery] = useState(params.get("q") ?? "");
  const [department, setDepartment] = useState(params.get("department") ?? "");
  const trimmedQuery = query.trim();
  const debouncedQuery = useDebouncedValue(trimmedQuery, 180);

  useEffect(() => {
    setError(null);
    void Promise.all([fetchProfessorIndex(), fetchDepartments()])
      .then(([nextProfessors, nextDepartments]) => {
        startTransition(() => {
          setProfessors(nextProfessors);
          setDepartments(nextDepartments);
        });
      })
      .catch((nextError) => {
        setError(nextError instanceof Error ? nextError.message : "Could not load professor search.");
      });
  }, [reloadKey]);

  useEffect(() => {
    function focusSearch(event: KeyboardEvent) {
      const target = event.target;
      const isEditableTarget = target instanceof HTMLInputElement
        || target instanceof HTMLTextAreaElement
        || target instanceof HTMLSelectElement
        || (target instanceof HTMLElement && target.isContentEditable);
      if (event.key !== "/" || event.metaKey || event.ctrlKey || event.altKey || isEditableTarget) {
        return;
      }
      event.preventDefault();
      inputRef.current?.focus();
    }

    document.addEventListener("keydown", focusSearch);
    return () => document.removeEventListener("keydown", focusSearch);
  }, []);

  useEffect(() => {
    setQuery(params.get("q") ?? "");
    setDepartment(params.get("department") ?? "");
  }, [params]);

  const preparedProfessors = useMemo(() => prepareProfessorSearchEntries(professors), [professors]);
  const hasActiveFilters = Boolean(debouncedQuery || department);
  const searchResults = useMemo(() => {
    if (!hasActiveFilters) {
      return {
        results: professors.slice(0, DEFAULT_VISIBLE_PROFESSORS),
        totalMatches: professors.length,
      };
    }

    return searchProfessorEntries(preparedProfessors, debouncedQuery, department);
  }, [debouncedQuery, department, hasActiveFilters, preparedProfessors, professors]);
  const {
    anchorRef,
    hasMore,
    loadMore,
    visibleCount,
    visibleItems: visibleProfessors,
  } = useInfiniteResults(
    searchResults.results,
    hasActiveFilters ? SEARCH_BATCH_SIZE : DEFAULT_VISIBLE_PROFESSORS,
    SEARCH_BATCH_SIZE,
  );
  const isUpdatingResults = trimmedQuery !== debouncedQuery;
  const selectedDepartment = departments.find((candidate) => candidate.code === department);

  if (error) {
    return <StaticDataState title="Professor search could not load." message={error} error onRetry={() => setReloadKey((value) => value + 1)} />;
  }

  if (professors.length === 0 && departments.length === 0) {
    return <StaticDataState title="Loading professor search..." />;
  }

  return (
    <section className="stack-lg">
      <div className="panel stack-md">
        <div className="section-heading">
          <p className="eyebrow">Professor Search</p>
          <h1>Search Dartmouth professors</h1>
        </div>
        <p className="search-note">
          Looking for a class instead? Go back to <Link to="/search/">course search</Link>. Press <kbd>/</kbd> to focus search.
        </p>
        {selectedDepartment ? (
          <p className="notice-banner">
            Showing professors mentioned in {selectedDepartment.name || selectedDepartment.code} reviews.
          </p>
        ) : null}
        <form
          className="search-bar course-search-bar"
          onSubmit={(event) => {
            event.preventDefault();
            const nextParams = new URLSearchParams();
            if (query.trim()) {
              nextParams.set("q", query.trim());
            }
            if (department) {
              nextParams.set("department", department);
            }
            setParams(nextParams);
          }}
        >
          <input
            ref={inputRef}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Try Nina Pavcnik, Doug Irwin, or Andrew Campbell"
          />
          <select value={department} onChange={(event) => setDepartment(event.target.value)}>
            <option value="">All departments</option>
            {departments.map((departmentOption) => (
              <option key={departmentOption.code} value={departmentOption.code}>
                {departmentOption.code} - {departmentOption.name}
              </option>
            ))}
          </select>
          <button type="submit">Search</button>
        </form>
      </div>

      {hasActiveFilters ? (
        <p className="toolbar-summary">
          {isUpdatingResults
            ? "Updating results..."
            : `${searchResults.totalMatches} professor${searchResults.totalMatches === 1 ? "" : "s"} found`}
        </p>
      ) : null}

      <div className="results-grid">
        {visibleProfessors.map((professor) => (
          <Link key={professor.slug} className="result-card professor-result-card" to={`/professor/${professor.slug}/`}>
            <strong>{professor.name}</strong>
            <span>
              {professor.reviewCount} review{professor.reviewCount === 1 ? "" : "s"} across {professor.courseCount} course
              {professor.courseCount === 1 ? "" : "s"}
            </span>
            <small>{professor.departments.join(", ")}</small>
            {professor.mostRecentTerm ? <small>Most recent offering: {professor.mostRecentTerm}</small> : null}
          </Link>
        ))}
      </div>
      {hasMore ? (
        <div className="results-footer">
          <p className="toolbar-summary">
            Showing {visibleCount} of {searchResults.totalMatches} professors
          </p>
          <button type="button" className="ghost-button" onClick={loadMore}>
            Load more
          </button>
          <div ref={anchorRef} className="results-sentinel" aria-hidden="true" />
        </div>
      ) : null}
      {visibleProfessors.length === 0 ? (
        <p className="notice-banner">No professors match that search yet. Try a full name, nickname, initial, or nearby spelling.</p>
      ) : null}
    </section>
  );
}
